import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { Box, Card, CardContent, IconButton, Stack, Typography, useTheme } from '@mui/material';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import MenuButton from '../../General/Menu/MenuButton/MenuButton';

interface CardBoxProps {
    title?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
    selected?: boolean;
    menu?: React.ReactNode;
    rightButtons?: React.ReactNode;
    leftButtons?: React.ReactNode;
    style?: React.CSSProperties;
    onClick?: (evt: React.MouseEvent<HTMLDivElement>) => void;
    onSelectChange?: (state: boolean) => void;
    onDragOver?: (evt: React.DragEvent<HTMLDivElement>) => void;
    onDrop?: (evt: React.DragEvent<HTMLDivElement>) => void;
    highlightLeftBar?: boolean;
    showIcon?: boolean;
    icon?: React.ReactNode;
}

function CardBox(props: CardBoxProps) {
    const theme = useTheme();
    const offline = useSelector((state: AppState) => state.client.offline);

    const handleClick = () => {
        if (props.onSelectChange) {
            props.onSelectChange(!props.selected);
        }
    };

    return (
        <Card
            elevation={1}
            sx={{
                position: 'relative',
                overflow: props.highlightLeftBar ? 'hidden' : undefined,
                display: 'flex',
                flexDirection: 'column',
                background: `linear-gradient(135deg, 
          ${theme.palette.background.paper} 0%, 
          ${
              theme.palette.mode === 'dark' ? `${theme.palette.primary.dark}15` : `${theme.palette.primary.light}08`
          } 50%, 
          ${theme.palette.background.paper} 100%)`,
                p: 3,
                borderRadius: '12px',
                flexGrow: 1,
                ...props.style,
            }}
            onDragOver={props.onDragOver}
            onDrop={props.onDrop}
        >
            {props.highlightLeftBar && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        bgcolor: theme.palette.primary.main,
                        borderTopLeftRadius: theme.shape.borderRadius,
                        borderBottomLeftRadius: theme.shape.borderRadius,
                    }}
                />
            )}

            {(props.onSelectChange || props.menu || props.title) && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                        pb: 1,
                        userSelect: 'none',
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {props.onSelectChange && (
                            <IconButton size="small" onClick={handleClick}>
                                {props.selected ? <CheckBox /> : <CheckBoxOutlineBlank />}
                            </IconButton>
                        )}
                        {props.leftButtons}
                    </Stack>

                    <Box
                        onClick={props.onClick}
                        sx={{
                            flexGrow: 1,
                            textAlign: 'left',
                            cursor: props.onClick ? 'pointer' : 'default',
                        }}
                    >
                        {props.title && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {props.showIcon && props.icon && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                                        {props.icon}
                                    </Box>
                                )}
                                <Typography
                                    variant="h4"
                                    sx={{
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {props.title}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    <Stack direction="row" alignItems="center" spacing={1}>
                        {!offline && props.menu && <MenuButton>{props.menu}</MenuButton>}
                        {props.rightButtons}
                    </Stack>
                </Box>
            )}

            <CardContent sx={{ p: 0, '@media (max-width: 600px)': { p: 0 } }}>{props.children}</CardContent>
        </Card>
    );
}

export default CardBox;
